import { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ButtonGroup, ButtonGroupItem } from '@predoc/cow';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  ArrowUp,
  FullscreenIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - only in browser environment
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
}

// PDF.js options for cMaps and standard fonts
const pdfOptions = {
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/standard_fonts/',
};

interface PDFViewerProps {
  file: string | File | { url: string };
  className?: string;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.25;

export const PDFViewer = ({ file, className }: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Handle document load success
  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  // Handle document load error
  const handleDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
  };

  // Zoom controls
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE));
  };

  const handleFitToWidth = () => {
    setScale(1);
  };

  // Scroll to top
  const handleScrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigate to previous page
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      scrollToPage(newPage);
    }
  };

  // Navigate to next page
  const handleNextPage = () => {
    if (currentPage < numPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      scrollToPage(newPage);
    }
  };

  // Scroll to a specific page
  const scrollToPage = (pageNum: number) => {
    const pageElement = pageRefs.current.get(pageNum);
    if (pageElement && containerRef.current) {
      const containerTop = containerRef.current.getBoundingClientRect().top;
      const pageTop = pageElement.getBoundingClientRect().top;
      const offset = pageTop - containerTop + containerRef.current.scrollTop;
      containerRef.current.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  // Set page ref callback
  const setPageRef = useCallback(
    (pageNum: number) => (el: HTMLDivElement | null) => {
      if (el) {
        pageRefs.current.set(pageNum, el);
      } else {
        pageRefs.current.delete(pageNum);
      }
    },
    [],
  );

  // Track current page based on scroll position using Intersection Observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    const observerOptions = {
      root: container,
      rootMargin: '-50% 0px -50% 0px', // Trigger when page is in center of viewport
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const pageNum = parseInt(
            entry.target.getAttribute('data-page-number') || '1',
            10,
          );
          setCurrentPage(pageNum);
        }
      });
    }, observerOptions);

    // Observe all page elements
    pageRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [numPages]);

  // Track container width for responsive sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className={cn('relative flex h-full w-full flex-col', className)}>
      {/* PDF Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-neutral-700"
        role="document"
        aria-label="PDF document viewer"
      >
        <Document
          file={file}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          options={pdfOptions}
          loading={
            <div className="flex h-full items-center justify-center p-8">
              <div className="text-white">Loading PDF...</div>
            </div>
          }
          error={
            <div className="flex h-full items-center justify-center p-8">
              <div className="text-red-400">Failed to load PDF</div>
            </div>
          }
        >
          <div className="flex flex-col items-center gap-4 p-4">
            {Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <div
                  key={pageNumber}
                  ref={setPageRef(pageNumber)}
                  data-page-number={pageNumber}
                  className="shadow-lg"
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    width={containerWidth > 0 ? containerWidth - 64 : undefined}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </div>
              );
            })}
          </div>
        </Document>
      </div>

      {/* Right Side Controls - Zoom */}
      <div className="absolute right-4 bottom-4 z-10 flex flex-col gap-3">
        <ButtonGroup orientation="vertical" variant="dark">
          <ButtonGroupItem
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
            aria-label="Zoom in"
            tabIndex={0}
          >
            <Plus className="size-5" />
          </ButtonGroupItem>
          <ButtonGroupItem
            onClick={handleFitToWidth}
            aria-label="Fit to width"
            tabIndex={0}
          >
            <FullscreenIcon className="size-5" />
          </ButtonGroupItem>
          <ButtonGroupItem
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
            aria-label="Zoom out"
            tabIndex={0}
          >
            <Minus className="size-5" />
          </ButtonGroupItem>
        </ButtonGroup>

        {/* Scroll to Top - Separate Button */}
        <ButtonGroup orientation="horizontal" variant="dark">
          <ButtonGroupItem
            onClick={handleScrollToTop}
            aria-label="Scroll to top"
            tabIndex={0}
          >
            <ArrowUp className="size-5" />
          </ButtonGroupItem>
        </ButtonGroup>
      </div>

      {/* Pagination Controls - Bottom Left */}
      <div className="absolute bottom-4 left-4 z-10 flex items-center gap-3">
        <ButtonGroup orientation="horizontal" variant="dark">
          <ButtonGroupItem
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            tabIndex={0}
          >
            <ChevronLeft className="size-5" />
          </ButtonGroupItem>
          <ButtonGroupItem
            onClick={handleNextPage}
            disabled={currentPage >= numPages}
            aria-label="Next page"
            tabIndex={0}
          >
            <ChevronRight className="size-5" />
          </ButtonGroupItem>
        </ButtonGroup>
        <span
          className="rounded-full bg-neutral-900 px-4 py-2 text-sm font-medium text-white shadow-lg"
          aria-live="polite"
          aria-atomic="true"
        >
          Page {currentPage} of {numPages}
        </span>
      </div>
    </div>
  );
};
